import { DAYS_IN_WEEK } from '@/utils/dateHelpers';
import { startCase } from 'lodash';
import { Country, ICountry } from '../country';
import { HrDepartment, IHrDepartment } from '../hr-department';
import { EmployeeWorkgroup, IEmployeeWorkgroup } from '../employee-workgroup';
import {
  DEFAULT_WORKDAY_HOURS,
  EmployeeHourlySubType,
  EmployeeSalarySubType,
  EmployeeType,
  IEmployee,
  IEmployeeData,
  IEmployeeFieldError,
  IEmployeeFieldErrorData,
  IEmployeeTree,
  IEmployeeWorkSchedule,
  IEmployeeWorkScheduleData,
  IEmployeeWorkScheduleFieldError,
  IEmployeeWorkScheduleFieldErrorData,
  IOOOInfo,
  IOOOInfoData,
  IScheduleWorkDay,
  IScheduleWorkDayData,
  IScheduleWorkDayFieldError,
  IScheduleWorkDayFieldErrorData
} from './employee.types';

export class Employee implements IEmployee {
  readonly id?: number;
  readonly first_name: string;
  readonly last_name: string;
  /** TODO: LH - 2023-02-10 - intentionally deviated from backed as backend does not return type in every seralized case but is expecting the emplyee to implecitly be a Salary type */
  readonly type: number = EmployeeType.Salary;
  readonly subtype: number = EmployeeHourlySubType.DayShift;

  readonly ooo_info?: null | OOOInfo = null;

  readonly workday_hours?: number;

  readonly employee_number: string | null = null;
  readonly title?: string;
  readonly hire_date?: string;
  readonly end_date?: string;
  readonly phone: string = '';

  readonly is_active?: boolean = true;
  readonly supervisor?: IEmployee | null | number = null;
  readonly user?: null | number = null;

  readonly location?: ICountry | null | number = null;
  readonly hr_department?: IHrDepartment | null | number = null;
  readonly workgroup: IEmployeeWorkgroup | null | number = null;

  constructor(data: IEmployeeData) {
    if (data.id) {
      this.id = data.id;
    }
    this.first_name = data.first_name;

    this.type = data.type ?? EmployeeType.Salary;

    this.subtype = data.subtype ?? EmployeeHourlySubType.DayShift;

    this.last_name = data.last_name;

    this.employee_number = data.employee_number ?? null;

    this.is_active = data.is_active ?? true;

    if (data.title) {
      this.title = data.title;
    }

    if (data.ooo_info) {
      this.ooo_info = new OOOInfo(data.ooo_info);
    }

    if (data.supervisor) {
      this.supervisor = new Employee(data.supervisor as IEmployeeData);
    }

    if (data.user) {
      this.user = data.user;
    }

    if (data.hire_date) {
      this.hire_date = data.hire_date;
    }

    if (data.end_date) {
      this.end_date = data.end_date;
    }

    if (data.phone) {
      this.phone = data.phone;
    }

    if (data.location) {
      if (typeof data.location === 'number') {
        this.location = data.location;
      } else {
        this.location = new Country(data.location);
      }
    }

    if (data.hr_department) {
      if (typeof data.hr_department === 'number') {
        this.hr_department = data.hr_department;
      } else {
        this.hr_department = new HrDepartment(data.hr_department);
      }
    }

    if (data.workgroup) {
      if (typeof data.workgroup === 'number') {
        this.workgroup = data.workgroup;
      } else {
        this.workgroup = new EmployeeWorkgroup(data.workgroup);
      }
    }
  }

  validate(): boolean {
    if (this.first_name && this.last_name && this.employee_number) {
      return true;
    } else {
      return false;
    }
  }

  get fullName(): string {
    return `${this.first_name} ${this.last_name}`.trim();
  }

  get intitials(): string {
    return this.fullName
      .toUpperCase()
      .split(' ')
      .map(n => n[0])
      .join('');
  }

  toString(): string {
    let stringRepresentation = this.fullName;

    if (this.isHourly && this.employee_number) {
      stringRepresentation += ` (${this.employee_number})`;
    }

    return stringRepresentation;
  }

  subtypeToString(): string {
    switch (this.type) {
      case EmployeeType.Salary:
        return startCase(EmployeeSalarySubType[this.subtype]);
      case EmployeeType.Hourly:
      default:
        return startCase(EmployeeHourlySubType[this.subtype]);
    }
  }

  get isHourly(): boolean {
    return this.type == EmployeeType.Hourly;
  }
}

export class EmployeeTree extends Employee implements IEmployeeTree {
  readonly children: IEmployeeTree[] = [];

  constructor(data: IEmployeeData) {
    super(data);
  }

  static genTree(employees: IEmployee[]) {
    const employeeList = employees
      .filter(employee => employee.is_active)
      .map(employee => new EmployeeTree(employee));

    // map linking the employee with its supervisor
    const employeeMap = new Map<number, number>();

    const employeeRoots = [];

    // first iteration to set the employee map
    for (let index = 0; index < employeeList.length; index++) {
      employeeMap.set(employeeList[index].id!, index);
    }

    // second iteration to set the supervisors to the correct employee map
    for (let index = 0; index < employeeList.length; index++) {
      const node = employeeList[index];
      if (node.supervisor) {
        const nodeSupervisorId = employeeMap.get(
          (node.supervisor as IEmployee).id!
        )!;

        if (nodeSupervisorId) {
          employeeList[nodeSupervisorId].children.push(node);
        }
      } else {
        employeeRoots.push(node);
      }
    }

    employeeRoots.sort(
      (employeeA, employeeB) =>
        employeeB.children.length - employeeA.children.length
    );

    return employeeRoots;
  }
}

export class OOOInfo implements IOOOInfo {
  readonly alternates?: IEmployee[];

  constructor(data: IOOOInfoData) {
    if (data.alternates) {
      this.alternates = data.alternates.map(
        alternate => new Employee(alternate)
      );
    }
  }

  validate(): boolean {
    throw new Error('Method not implemented.');
  }
}

export class ScheduleWorkDay implements IScheduleWorkDay {
  readonly workday: number;

  constructor(data: IScheduleWorkDayData) {
    this.workday = data.workday;
  }
  validate(): boolean {
    throw new Error('Method not implemented.');
  }
}

export class EmployeeWorkSchedule implements IEmployeeWorkSchedule {
  employee?: number;
  scheduled_days: (IScheduleWorkDay | number)[] = [];
  readonly workday_hours?: number = DEFAULT_WORKDAY_HOURS;

  constructor(data?: IEmployeeWorkScheduleData) {
    if (data) {
      this.employee = data.employee;
      this.workday_hours = data.workday_hours ?? DEFAULT_WORKDAY_HOURS;

      if (data.scheduled_days) {
        this.scheduled_days = data.scheduled_days.map(scheduledDay => {
          if (typeof scheduledDay == 'number') {
            return scheduledDay;
          } else {
            return new ScheduleWorkDay(scheduledDay);
          }
        });
      }
    }
  }

  get rruleScheduledDays(): number[] {
    return this.scheduled_days.map(scheduledDay => {
      return (
        (DAYS_IN_WEEK + (scheduledDay as IScheduleWorkDay).workday - 1) %
        DAYS_IN_WEEK
      );
    });
  }

  validate(): boolean {
    throw new Error('Method not implemented.');
  }
}

export class ScheduleWorkDayFieldError implements IScheduleWorkDayFieldError {
  workday: string[] = [];

  constructor(data?: IScheduleWorkDayFieldErrorData) {
    if (data) {
      if (data.workday) {
        this.workday = data.workday;
      }
    }
  }

  clearErrors(): void {
    this.workday.splice(0, this.workday.length);
  }

  static isScheduleWorkDayFieldError(
    data: IScheduleWorkDayFieldErrorData
  ): boolean {
    if (data.workday && Array.isArray(data.workday)) {
      return true;
    }
    return false;
  }
}

export class EmployeeWorkScheduleFieldError
  implements IEmployeeWorkScheduleFieldError {
  readonly employee: string[] = [];
  readonly scheduled_days: IScheduleWorkDayFieldError[] = [];
  readonly workday_hours: string[] = [];

  constructor(data?: IEmployeeWorkScheduleFieldErrorData) {
    if (data) {
      if (data.employee) {
        this.employee = data.employee;
      }
      if (data.scheduled_days) {
        this.scheduled_days = data.scheduled_days.map(
          scheduledDay => new ScheduleWorkDayFieldError(scheduledDay)
        );
      }
      if (data.workday_hours) {
        this.workday_hours = data.workday_hours;
      }
    }
  }

  clearErrors(): void {
    this.employee.splice(0, this.employee.length);
    this.workday_hours.splice(0, this.workday_hours.length);
  }

  static isEmployeeWorkScheduleFieldError(
    data: IEmployeeWorkScheduleFieldErrorData
  ): boolean {
    if (data.employee && Array.isArray(data.employee)) {
      return true;
    }
    if (data.workday_hours && Array.isArray(data.workday_hours)) {
      return true;
    }
    return false;
  }
}

export class EmployeeFieldError implements IEmployeeFieldError {
  readonly first_name: string[] = [];
  readonly last_name: string[] = [];
  readonly employee_number: string[] = [];
  readonly is_active: string[] = [];
  readonly title: string[] = [];
  readonly type: string[] = [];
  readonly subtype: string[] = [];
  readonly hire_date: string[] = [];
  readonly end_date: string[] = [];
  readonly supervisor: string[] = [];
  readonly user: string[] = [];
  readonly location: string[] = [];
  readonly hr_department: string[] = [];
  readonly workgroup: string[] = [];
  readonly phone: string[] = [];

  constructor(data?: IEmployeeFieldErrorData) {
    if (data) {
      if (data.first_name) {
        this.first_name = data.first_name;
      }
      if (data.last_name) {
        this.last_name = data.last_name;
      }
      if (data.employee_number) {
        this.employee_number = data.employee_number;
      }
      if (data.is_active) {
        this.is_active = data.is_active;
      }
      if (data.type) {
        this.type = data.type;
      }
      if (data.subtype) {
        this.subtype = data.subtype;
      }
      if (data.hire_date) {
        this.hire_date = data.hire_date;
      }
      if (data.end_date) {
        this.end_date = data.end_date;
      }
      if (data.title) {
        this.title = data.title;
      }
      if (data.supervisor) {
        this.supervisor = data.supervisor;
      }
      if (data.user) {
        this.user = data.user;
      }
      if (data.location) {
        this.location = data.location;
      }
      if (data.hr_department) {
        this.hr_department = data.hr_department;
      }
      if (data.workgroup) {
        this.workgroup = data.workgroup;
      }
      if (data.phone) {
        this.phone = data.phone;
      }
    }
  }

  clearErrors(): void {
    this.first_name.splice(0, this.first_name.length);
    this.last_name.splice(0, this.last_name.length);
    this.employee_number.splice(0, this.employee_number.length);
    this.is_active.splice(0, this.is_active.length);
    this.type.splice(0, this.type.length);
    this.subtype.splice(0, this.subtype.length);
    this.title.splice(0, this.title.length);
    this.hire_date.splice(0, this.hire_date.length);
    this.supervisor.splice(0, this.supervisor.length);
    this.user.splice(0, this.user.length);
    this.location.splice(0, this.location.length);
    this.hr_department.splice(0, this.hr_department.length);
    this.workgroup.splice(0, this.workgroup.length);
    this.phone.splice(0, this.phone.length);
  }

  static isEmployeeFieldError(data: IEmployeeFieldErrorData): boolean {
    if (data.first_name && Array.isArray(data.first_name)) {
      return true;
    } else if (data.last_name && Array.isArray(data.last_name)) {
      return true;
    } else if (data.employee_number && Array.isArray(data.employee_number)) {
      return true;
    } else if (data.is_active && Array.isArray(data.is_active)) {
      return true;
    } else if (data.type && Array.isArray(data.type)) {
      return true;
    } else if (data.subtype && Array.isArray(data.subtype)) {
      return true;
    } else if (data.title && Array.isArray(data.title)) {
      return true;
    } else if (data.hire_date && Array.isArray(data.hire_date)) {
      return true;
    } else if (data.supervisor && Array.isArray(data.supervisor)) {
      return true;
    } else if (data.user && Array.isArray(data.user)) {
      return true;
    } else if (data.location && Array.isArray(data.location)) {
      return true;
    } else if (data.hr_department && Array.isArray(data.hr_department)) {
      return true;
    } else if (data.workgroup && Array.isArray(data.workgroup)) {
      return true;
    } else if (data.phone && Array.isArray(data.phone)) {
      return true;
    }

    return false;
  }
}

export class EmployeeNode {
  value: IEmployee;
  parent: EmployeeNode | null = null;
  readonly children: EmployeeNode[] = [];

  constructor(value: IEmployee, parent: EmployeeNode | null = null) {
    this.value = value;
    this.parent = parent;
  }

  addChild(node: EmployeeNode) {
    this.children.push(node);
  }
}

export class EmployeeOrgChart {
  trees: EmployeeNode[] = [];

  constructor(employees: IEmployee[]) {
    this.trees = this.buildTrees(employees);
  }

  buildTrees(nodes: IEmployee[], id: number | null = null): EmployeeNode[] {
    return (
      // filter employees if the supervisor matches the id given that need to be iterated through
      nodes
        .filter(node => {
          const supervisorId =
            (node.supervisor && (node?.supervisor as IEmployee).id!) ?? null;

          return supervisorId == id;
        })
        .map(employee => {
          const employeeNode = new EmployeeNode(employee);

          // recurse for each filtered node to add each node to their respective parent
          this.buildTrees(nodes, employeeNode.value.id).forEach(tree => {
            employeeNode.addChild(tree);
          });

          return employeeNode;
        })
        // sort each tree from the most children first
        .sort((treeA, treeB) => treeB.children.length - treeA.children.length)
    );
  }
}
